<template>
  <footer
    id="footer-wrap"
    :class="[
      isMobile ? 'cssfooterMobileApp' : 'cssfooter',
      isMobileScreen ? 'setFooterLineHeightMobile' : 'setFooterLineHeight',
    ]"
  >
    <v-toolbar class="elevation-0 justify-center" aria-label="footer">
      <v-row justify="center" class="align-center ml-0 mr-0">
        <v-col cols="12" class="px-2 pb-2 mt-1 footerHeight">
          <a
            v-for="(link, index) in footerLinks"
            :key="index"
            :href="link.linkUrl ? link.linkUrl : 'javascript:void(0)'"
            :target="link.linkUrl ? '_blank' : ''"
            :class="index === 0 ? 'font-weight-bold no-underline' : ''"
            :aria-label="
              link.linkUrl ? $t('L_opens_new_window') + $t(link.linkName) : $t(link.linkName)
            "
            @click="callMethod(index)"
          >
            {{ $t(link.linkName) }}
          </a>
          <div
            class="copyright"
            :class="$vuetify.display.xs ? 'font-size--12 pt-1' : 'font-size--14'"
          >
            &#x24B8;{{ new Date().getFullYear() }} {{ $t('primericaweburl') }}
          </div>
        </v-col>
        <TermsOfUse
          v-if="showTermsOfUseModal"
          :terms-of-use-modal="showTermsOfUseModal"
          :terms-of-use-content="termsOfUseContent"
          @hidetermsOfUseModal="showTermsOfUseModal = false"
        />
      </v-row>
    </v-toolbar>
  </footer>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { useGlobalStore } from '@/stores/global/global.store';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import TermsOfUse from '@/components/TermsOfUse/TermsOfUse.vue';
import { mapStores } from 'pinia';
import { RouteNames } from '@/router/routeNames';
import { getTermsOfUseContent } from '@/shared/api/content.ts';
import { $t } from '@/i18n/i18n.ts';

export const termsOfUseContentUrl =
  'https://assets.contentstack.io/v3/assets/blt1dedeb682e8df2a2/blt19dba62bbf957b2c/MISC-011_Sep2021.html';
export const termsOfUseContentUrlFR =
  'https://assets.contentstack.io/v3/assets/blt1dedeb682e8df2a2/bltd2ddced11e64590f/MISC-012_September2021.html';
export default defineComponent({
  name: 'FooterComponent',
  components: {
    TermsOfUse,
  },
  data: () => ({
    showTermsOfUseModal: false,
    termsOfUseContent: '',
  }),
  computed: {
    ...mapStores(useGlobalStore),
    isMobile() {
      return CapacitorFacade.isNativePlatform();
    },
    isMobileScreen() {
      return this.$vuetify.display.xs;
    },
    footerLinks() {
      const _footerLinks = {
        enUS: [
          { linkName: 'L_footer_primerica', linkUrl: 'https://www.primerica.com/public/' },
          {
            linkName: 'L_footer_privacy',
            linkUrl: 'http://www.primerica.com/public/privacy/privacy_notices.html',
          },
          {
            linkName: 'L_footer_importantdisclosures',
            linkUrl: 'http://www.primerica.com/public/primerica_disclosures.html',
          },
          {
            linkName: 'L_footer_termsofuse',
            linkUrl:
              'https://assets.contentstack.io/v3/assets/blt1dedeb682e8df2a2/blt19dba62bbf957b2c/MISC-011_Sep2021.html',
          },
          { linkName: 'L_footer_contact', linkUrl: 'http://www.primerica.com/public/contact.html' },
          {
            linkName: 'L_footer_adastatement',
            linkUrl: 'http://www.primerica.com/public/ADA_Policy_Statement.html',
          },
          { linkName: 'L_footer_domestic_violence_confidentiality', linkUrl: '' },
          {
            linkName: 'L_footer_pfsidisclosures',
            linkUrl: 'https://www.primerica.com/public/pfsi-disclosures.html',
          },
          {
            linkName: 'L_footer_formcrs',
            linkUrl: 'https://www.primerica.com/public/form-crs.html',
          },
        ],
        enCA: [
          { linkName: 'L_footer_primerica', linkUrl: 'https://www.primericacanada.ca/' },
          {
            linkName: 'L_footer_privacy',
            linkUrl: 'http://www.primericacanada.ca/public/canada/canada_privacy.html',
          },
          {
            linkName: 'L_footer_importantdisclosures',
            linkUrl: 'http://www.primerica.com/public/primerica_disclosures.html',
          },
          {
            linkName: 'L_footer_termsofuse',
            linkUrl:
              'https://assets.contentstack.io/v3/assets/blt1dedeb682e8df2a2/blt19dba62bbf957b2c/MISC-011_Sep2021.html',
          },
          {
            linkName: 'L_footer_contact',
            linkUrl: 'http://www.primericacanada.ca/public/canada/contact.html',
          },
          {
            linkName: 'L_footer_accessibility',
            linkUrl:
              'http://www.primericacanada.ca/public/canada/primerica_disclosures.html#accessibility',
          },
          {
            linkName: 'L_footer_fairtreatmentofcustomers',
            linkUrl: 'http://www.primericacanada.ca/public/canada/fair-treatment-of-customers.html',
          },
        ],
        frCA: [
          {
            linkName: 'L_footer_primerica',
            linkUrl: 'https://www.primericacanada.ca/public/canada/french/index.html',
          },
          {
            linkName: 'L_footer_privacy',
            linkUrl: 'http://www.primericacanada.ca/public/canada/french/canada_privacy.html',
          },
          {
            linkName: 'L_footer_importantdisclosures',
            linkUrl:
              'http://www.primericacanada.ca/public/canada/french/primerica_disclosures.html',
          },
          {
            linkName: 'L_footer_termsofuse',
            linkUrl:
              'https://assets.contentstack.io/v3/assets/blt1dedeb682e8df2a2/bltd2ddced11e64590f/MISC-012_September2021.html',
          },
          {
            linkName: 'L_footer_contact',
            linkUrl: 'http://www.primericacanada.ca/public/canada/french/contact.html',
          },
          {
            linkName: 'L_footer_accessibility',
            linkUrl:
              'http://www.primericacanada.ca/public/canada/french/primerica_disclosures.html#accessibility',
          },
          {
            linkName: 'L_footer_fairtreatmentofcustomers',
            linkUrl:
              'http://www.primericacanada.ca/public/canada/french/fair-treatment-of-customers-fr.html',
          },
        ],
      };
      if (this.globalStore.locale.valueOf() === 'en_CA') {
        return _footerLinks.enCA;
      } else if (this.globalStore.locale.valueOf() === 'fr_CA') {
        return _footerLinks.frCA;
      }
      return _footerLinks.enUS;
    },
    termsOfUseContentUrl() {
      if (this.globalStore.locale.includes('fr')) {
        return termsOfUseContentUrlFR;
      }
      return termsOfUseContentUrl;
    },
  },
  methods: {
    $t,
    async openTermsOfUseModal() {
      this.showTermsOfUseModal = true;
      this.termsOfUseContent = await getTermsOfUseContent(this.termsOfUseContentUrl as string);
    },
    async goToDomesticViolenceCard() {
      await this.$router.push({ name: RouteNames.DOMESTIC_VIOLENCE });
    },
    async callMethod(index: number) {
      if (index === 6 && this.globalStore.locale.valueOf() === 'en_US') {
        await this.goToDomesticViolenceCard();
      }
    },
  },
});
</script>

<style lang="scss">
.cssfooter {
  text-align: center;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  height: auto;
  padding: 0.5rem 0 1.25rem;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;

  .theme--light.v-toolbar {
    background-color: #ffffff;
  }

  a {
    color: #666666 !important;
    text-decoration: underline;
    padding-right: 8px;
    font-weight: 400;
  }
}

.cssfooterMobileApp {
  text-align: center;
  background-color: #ffffff;
  height: auto;
  padding: 0.5rem 0 1.25rem;
  position: absolute;
  bottom: 100px;
  left: 0;
  right: 0;

  .theme--light.v-toolbar {
    background-color: #ffffff;
  }

  a {
    color: #666666 !important;
    text-decoration: underline;
    padding-right: 8px;
    font-weight: 400;
  }
}

.copyright {
  font-size: 12px;
  color: #666666 !important;
}

#footer-wrap .v-toolbar__content {
  background: #ffffff;
  height: auto !important;
  padding: 0 24px 16px;
}

#footer-wrap {
  z-index: 9999;
}

.wordBreak {
  word-break: break-word;
  overflow-wrap: break-word;
}

.no-underline {
  text-decoration: none !important;
}

.setFooterLineHeight {
  line-height: 1.5 !important;
  font-size: 14px;
}

.setFooterLineHeightMobile {
  line-height: 1.25 !important;
  font-size: 12px;
}

.height45 {
  height: 45px !important;
}

.footerHeight {
  height: auto !important;
}
</style>
